// ============================================================================
// CERTIFICATION DASHBOARD
// ============================================================================
// Purpose: Overview of all compliance certifications with enterprise-grade UI
// Mental model: "Central hub for certification management"
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { certificationApi, Certification } from '../api';
import { Award, Clock, CheckCircle, AlertTriangle, Search, Filter, Download, Eye, MoreVertical } from 'lucide-react';

interface CertificationStats {
  totalCertifications: number;
  activeCertifications: number;
  expiringSoon: number;
  expiredCertifications: number;
  suspendedCertifications: number;
}

const CertificationDashboard: React.FC = () => {
  const { user, hasPermission } = useAuth();
  // Only External Auditors actually issue certificates — they need the portal link.
  // Company Admins / Compliance Users may land on this page to view their certs,
  // but pointing them at /audit/dashboard is a dead end (wrong domain guard).
  const canIssueCertificates = hasPermission('certifications.manage') && hasPermission('audit.dashboard.view');
  const [stats, setStats] = useState<CertificationStats | null>(null);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [filteredCertifications, setFilteredCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCertification, setSelectedCertification] = useState<Certification | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    filterCertifications();
  }, [searchTerm, statusFilter, certifications]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch certifications list (works for all roles)
      const certList = user?.company_id
        ? await certificationApi.getCompanyCertifications(user.company_id)
        : await certificationApi.getAllCertifications();

      // Compute from the actual list — the stats endpoint uses a mix of
      // field naming conventions and doesn't return expiring-soon directly,
      // so deriving from the list is more reliable and always stays in sync
      // with what the user sees below.
      const now = new Date();
      const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const dashboardStats: CertificationStats = {
        totalCertifications: certList.length,
        activeCertifications: certList.filter(c => c.status === 'active').length,
        expiringSoon: certList.filter(c => c.status === 'active' && c.expiry_date && new Date(c.expiry_date) <= in30Days && new Date(c.expiry_date) >= now).length,
        expiredCertifications: certList.filter(c => c.status === 'expired' || (c.status === 'active' && c.expiry_date && new Date(c.expiry_date) < now)).length,
        suspendedCertifications: certList.filter(c => c.status === 'suspended').length,
      };

      setStats(dashboardStats);
      setCertifications(certList);
      setFilteredCertifications(certList);
    } catch (error) {
      console.error('Failed to load certification dashboard data:', error);
      setStats({
        totalCertifications: 0,
        activeCertifications: 0,
        expiringSoon: 0,
        expiredCertifications: 0,
        suspendedCertifications: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const filterCertifications = () => {
    let filtered = [...certifications];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(cert =>
        cert.certificate_number?.toLowerCase().includes(term) ||
        `Company ${cert.company_id}`.toLowerCase().includes(term) ||
        `Framework ${cert.framework_id}`.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(cert => cert.status === statusFilter);
    }

    setFilteredCertifications(filtered);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: any; label: string }> = {
      active: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Active' },
      expired: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, label: 'Expired' },
      revoked: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Clock, label: 'Revoked' },
      suspended: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertTriangle, label: 'Suspended' }
    };

    const config = statusConfig[status] || { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock, label: 'Unknown' };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const handleViewDetails = (cert: Certification) => {
    setSelectedCertification(cert);
    setShowDetailsModal(true);
  };

  const handleDownloadCertificate = async (cert: Certification) => {
    try {
      if (cert.certificate_url) {
        window.open(cert.certificate_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to download certificate:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-600 font-medium">Loading certifications...</p>
        </div>
      </div>
    );
  }

  const fmtDate = (s?: string | null) => s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const daysBetween = (a: Date, b: Date) => Math.floor((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
  const softCard: React.CSSProperties = { boxShadow: '0 4px 20px rgba(0,0,0,0.02)' };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Award className="text-blue-600" size={28} />
              <h1 className="text-3xl font-bold text-slate-900">Certifications</h1>
            </div>
            <p className="text-slate-500 text-sm mt-1">All compliance certificates visible to you — issued, active, expiring, and historical.</p>
          </div>
          {canIssueCertificates && (
            <button
              onClick={() => window.location.href = '/audit/dashboard'}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50"
            >
              <Award size={15} />
              Open Auditor Portal
            </button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Total', value: stats.totalCertifications, icon: <Award size={18} className="text-blue-600" />, accent: 'text-slate-900' },
              { label: 'Active', value: stats.activeCertifications, icon: <CheckCircle size={18} className="text-emerald-600" />, accent: 'text-emerald-700' },
              { label: 'Expiring ≤30d', value: stats.expiringSoon, icon: <Clock size={18} className="text-amber-600" />, accent: 'text-amber-700' },
              { label: 'Expired', value: stats.expiredCertifications, icon: <AlertTriangle size={18} className="text-red-600" />, accent: 'text-red-700' },
              { label: 'Suspended', value: stats.suspendedCertifications, icon: <AlertTriangle size={18} className="text-slate-500" />, accent: 'text-slate-700' },
            ].map(k => (
              <div key={k.label} className="bg-white rounded-2xl border border-slate-100 p-4" style={softCard}>
                <div className="flex items-center gap-2 mb-1">
                  {k.icon}
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{k.label}</p>
                </div>
                <p className={`text-3xl font-bold ${k.accent}`}>{k.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl border border-slate-100 p-3 mb-4 flex flex-col sm:flex-row gap-2" style={softCard}>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Search certificate number, company, or framework…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[180px]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        {/* Certifications Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={softCard}>
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Certifications ({filteredCertifications.length})</h2>
          </div>

          {filteredCertifications.length === 0 ? (
            <div className="py-16 text-center">
              <Award className="mx-auto text-slate-300 mb-3" size={48} />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No certifications found</h3>
              <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
                {canIssueCertificates
                  ? 'Certificates are issued from the External Auditor Portal once every control of a framework has been marked Satisfied.'
                  : 'Certifications will appear here once issued by your external auditor.'}
              </p>
              {canIssueCertificates && (
                <button
                  onClick={() => window.location.href = '/audit/dashboard'}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                >
                  <Award size={15} />
                  Open External Auditor Portal
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    <th className="px-5 py-3">Certificate</th>
                    <th className="px-5 py-3">Company</th>
                    <th className="px-5 py-3">Framework</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Issued</th>
                    <th className="px-5 py-3">Expires</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCertifications.map((cert) => {
                    const now = new Date();
                    const exp = cert.expiry_date ? new Date(cert.expiry_date) : null;
                    const days = exp ? daysBetween(now, exp) : null;
                    const expCls = !exp ? 'text-slate-500' : days! < 0 ? 'text-red-600 font-semibold' : days! <= 30 ? 'text-red-500 font-semibold' : days! <= 90 ? 'text-amber-600 font-semibold' : 'text-slate-600';
                    return (
                      <tr key={cert.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3">
                          <span className="font-bold text-slate-800">{cert.certificate_number}</span>
                        </td>
                        <td className="px-5 py-3 text-slate-700">{cert.company_name || `Company ${cert.company_id}`}</td>
                        <td className="px-5 py-3 text-slate-700">{cert.framework_name || `Framework ${cert.framework_id}`}</td>
                        <td className="px-5 py-3">{getStatusBadge(cert.status)}</td>
                        <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{fmtDate(cert.issue_date)}</td>
                        <td className={`px-5 py-3 whitespace-nowrap ${expCls}`}>
                          {fmtDate(cert.expiry_date)}
                          {days !== null && <span className="block text-[10px] font-normal text-slate-400">{days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`}</span>}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleViewDetails(cert)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="View Details">
                              <Eye size={16} />
                            </button>
                            <button onClick={() => handleDownloadCertificate(cert)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Download Certificate">
                              <Download size={16} />
                            </button>
                            <button className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors" title="More Options">
                              <MoreVertical size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedCertification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-3xl font-bold text-slate-900">Certificate Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-150"
                >
                  <MoreVertical size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Certificate Number</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedCertification.certificate_number}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Status</p>
                  {getStatusBadge(selectedCertification.status)}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Company</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedCertification.company_name || `Company ${selectedCertification.company_id}`}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Framework</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedCertification.framework_name || `Framework ${selectedCertification.framework_id}`}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Issue Date</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {selectedCertification.issue_date ? new Date(selectedCertification.issue_date).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Expiry Date</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {selectedCertification.expiry_date ? new Date(selectedCertification.expiry_date).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Compliance Score</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedCertification.overall_score || '-'}%</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Auditor</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedCertification.auditor_id || '-'}</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-3 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors duration-150"
              >
                Close
              </button>
              <button
                onClick={() => handleDownloadCertificate(selectedCertification)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                <Download size={20} />
                Download Certificate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationDashboard;
